import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Cancel,
  Schedule,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { confirmAppointment, rescheduleAppointment, cancelAppointment } from '../features/appointments/appointmentsSlice';

const AppointmentActions = ({ 
  appointment, 
  onUpdate, 
  canReschedule = true, 
  canCancel = true,
  canConfirm = false 
}) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openReschedule, setOpenReschedule] = useState(false);
  const [openCancel, setOpenCancel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({
    newDate: '',
    newTime: '',
    reason: ''
  });
  const [cancelData, setCancelData] = useState({
    reason: ''
  });

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleReschedule = () => {
    setOpenReschedule(true);
    handleMenuClose();
  };

  const handleCancel = () => {
    setOpenCancel(true);
    handleMenuClose();
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await dispatch(confirmAppointment(appointment.id)).unwrap();
      toast.success('Appointment confirmed successfully');
      onUpdate?.();
    } catch (error) {
      const errorMessage = typeof error === 'string' 
        ? error 
        : error?.message || error?.detail || 'Failed to confirm appointment';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleData.newDate || !rescheduleData.newTime) {
      toast.error('Please select both date and time');
      return;
    }

    if (!rescheduleData.reason?.trim()) {
      toast.error('Please provide a reason for rescheduling');
      return;
    }

    setLoading(true);
    try {
      await dispatch(rescheduleAppointment({
        id: appointment.id,
        new_date: rescheduleData.newDate,
        new_time: rescheduleData.newTime,
        reason: rescheduleData.reason
      })).unwrap();
      
      toast.success('Appointment rescheduled successfully');
      setOpenReschedule(false);
      setRescheduleData({ newDate: '', newTime: '', reason: '' });
      onUpdate?.();
    } catch (error) {
      const errorMessage = typeof error === 'string' 
        ? error 
        : error?.message || error?.detail || 'Failed to reschedule appointment';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubmit = async () => {
    if (!cancelData.reason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }

    setLoading(true);
    try {
      await dispatch(cancelAppointment({
        id: appointment.id,
        reason: cancelData.reason
      })).unwrap();
      
      toast.success('Appointment cancelled successfully');
      setOpenCancel(false);
      setCancelData({ reason: '' });
      onUpdate?.();
    } catch (error) {
      const errorMessage = typeof error === 'string' 
        ? error 
        : error?.message || error?.detail || 'Failed to cancel appointment';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isActionDisabled = () => {
    return ['Completed', 'Cancelled'].includes(appointment.status);
  };

  const getAvailableActions = () => {
    const actions = [];
    
    if (canConfirm && appointment.status === 'Pending') {
      actions.push({
        label: 'Confirm',
        icon: <CheckCircle />,
        onClick: handleConfirm,
        color: 'success'
      });
    }
    
    if (canReschedule && !isActionDisabled()) {
      actions.push({
        label: 'Reschedule',
        icon: <Schedule />,
        onClick: handleReschedule,
        color: 'primary'
      });
    }
    
    if (canCancel && !isActionDisabled()) {
      actions.push({
        label: 'Cancel',
        icon: <Cancel />,
        onClick: handleCancel,
        color: 'error'
      });
    }

    return actions;
  };

  const availableActions = getAvailableActions();

  if (availableActions.length === 0) {
    return null;
  }

  return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        disabled={isActionDisabled()}
        size="small"
      >
        <MoreVert />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {availableActions.map((action, index) => (
          <MenuItem
            key={index}
            onClick={action.onClick}
            sx={{ color: `${action.color}.main` }}
          >
            <ListItemIcon sx={{ color: `${action.color}.main` }}>
              {action.icon}
            </ListItemIcon>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Reschedule Dialog */}
      <Dialog open={openReschedule} onClose={() => setOpenReschedule(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Rescheduling will notify the patient and doctor of the change.
          </Alert>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="New Date"
              type="date"
              value={rescheduleData.newDate}
              onChange={(e) => setRescheduleData(prev => ({ ...prev, newDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            
            <TextField
              label="New Time"
              type="time"
              value={rescheduleData.newTime}
              onChange={(e) => setRescheduleData(prev => ({ ...prev, newTime: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            
            <TextField
              label="Reason for Rescheduling"
              multiline
              rows={3}
              value={rescheduleData.reason}
              onChange={(e) => setRescheduleData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Please provide a reason for rescheduling..."
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReschedule(false)}>Cancel</Button>
          <Button 
            onClick={handleRescheduleSubmit} 
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Reschedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={openCancel} onClose={() => setOpenCancel(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The patient will be notified of the cancellation.
          </Alert>
          
          <TextField
            label="Reason for Cancellation"
            multiline
            rows={3}
            value={cancelData.reason}
            onChange={(e) => setCancelData(prev => ({ ...prev, reason: e.target.value }))}
            placeholder="Please provide a reason for cancellation..."
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancel(false)}>Cancel</Button>
          <Button 
            onClick={handleCancelSubmit} 
            variant="contained"
            color="error"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Cancel Appointment
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppointmentActions;